import PropTypes from 'prop-types';
import { Grid, TextField, InputAdornment } from '@mui/material';
import {
    PersonOutlined,
    EmailOutlined,
    PhoneOutlined,
    CreditCardOutlined,
    CheckCircleOutlined,
    NumbersOutlined,
    AssignmentIndOutlined
} from '@mui/icons-material';
import SectionHeader from '../SectionHeader';

const PersonalDetailsSection = ({ form, handleChange, handleBlur, errors, touched }) => {
    return (
        <>
            <SectionHeader icon={PersonOutlined} title="Personal Details" subtitle="Basic identity information" />
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Employee ID"
                        required
                        fullWidth
                        placeholder="e.g. ABC/HR/AT/0001"
                        value={form.employeeId}
                        onChange={handleChange('employeeId')}
                        onBlur={handleBlur('employeeId')}
                        error={touched.employeeId && !!errors.employeeId}
                        helperText={touched.employeeId && errors.employeeId}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AssignmentIndOutlined
                                        fontSize="small"
                                        color={touched.employeeId && errors.employeeId ? 'error' : 'action'}
                                    />
                                </InputAdornment>
                            )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Full Name"
                        required
                        fullWidth
                        value={form.name}
                        onChange={handleChange('name')}
                        onBlur={handleBlur('name')}
                        error={touched.name && !!errors.name}
                        helperText={touched.name && errors.name}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonOutlined fontSize="small" color={touched.name && errors.name ? 'error' : 'action'} />
                                </InputAdornment>
                            )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Email Address"
                        required
                        fullWidth
                        type="email"
                        value={form.email}
                        onChange={handleChange('email')}
                        onBlur={handleBlur('email')}
                        onKeyDown={(e) => e.key === ' ' && e.preventDefault()}
                        error={touched.email && !!errors.email}
                        helperText={touched.email && errors.email}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailOutlined fontSize="small" color={touched.email && errors.email ? 'error' : 'action'} />
                                </InputAdornment>
                            )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Phone Number"
                        required
                        fullWidth
                        value={form.phone}
                        onChange={handleChange('phone')}
                        onBlur={handleBlur('phone')}
                        error={touched.phone && !!errors.phone}
                        helperText={touched.phone && errors.phone}
                        inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PhoneOutlined fontSize="small" color={touched.phone && errors.phone ? 'error' : 'action'} />
                                </InputAdornment>
                            )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="PAN Card Number"
                        required
                        fullWidth
                        value={form.pancard}
                        onChange={handleChange('pancard')}
                        onBlur={handleBlur('pancard')}
                        error={touched.pancard && !!errors.pancard}
                        helperText={touched.pancard && errors.pancard ? errors.pancard : 'Format: ABCDE1234F'}
                        inputProps={{ maxLength: 10, style: { letterSpacing: '0.12em' } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CreditCardOutlined fontSize="small" color={touched.pancard && errors.pancard ? 'error' : 'action'} />
                                </InputAdornment>
                            ),
                            endAdornment:
                                form.pancard.length === 10 && !errors.pancard ? (
                                    <InputAdornment position="end">
                                        <CheckCircleOutlined sx={{ color: 'success.main', fontSize: 18 }} />
                                    </InputAdornment>
                                ) : null
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Aadhaar Card Number"
                        required
                        fullWidth
                        value={form.aadharNumber}
                        onChange={handleChange('aadharNumber')}
                        onBlur={handleBlur('aadharNumber')}
                        error={touched.aadharNumber && !!errors.aadharNumber}
                        helperText={touched.aadharNumber && errors.aadharNumber ? errors.aadharNumber : 'Format: 12-digit number'}
                        inputProps={{ maxLength: 12, inputMode: 'numeric', style: { letterSpacing: '0.12em' } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <NumbersOutlined
                                        fontSize="small"
                                        color={touched.aadharNumber && errors.aadharNumber ? 'error' : 'action'}
                                    />
                                </InputAdornment>
                            ),
                            endAdornment:
                                form.aadharNumber.length === 12 && !errors.aadharNumber ? (
                                    <InputAdornment position="end">
                                        <CheckCircleOutlined sx={{ color: 'success.main', fontSize: 18 }} />
                                    </InputAdornment>
                                ) : null
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>
            </Grid>
        </>
    );
};

PersonalDetailsSection.propTypes = {
    form: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleBlur: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired
};

export default PersonalDetailsSection;
